import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import {
    ITagDTO,
    ITagUsageDTO,
    PersonCompanyRole,
    PersonContactType,
} from '@modeso/types__aumico-lib-companies-be';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { localizationKeys } from '../../constants/localizationKeys';
import { TasksUpdate } from '../../enums/tasks-update.enum';
import { IPersonForm } from '../../interfaces/IPersonForm.interface';

type CompanyLink = IPersonForm['companies'][number];
type ContactGroup = FormGroup<{ type: FormControl<PersonContactType>; value: FormControl<string> }>;

const ROLE_ORDER: PersonCompanyRole[] = [
    PersonCompanyRole.OWNER,
    PersonCompanyRole.SHAREHOLDER,
    PersonCompanyRole.MANAGING_DIRECTOR,
    PersonCompanyRole.BOARD_MEMBER,
    PersonCompanyRole.OTHER,
];

/** E-mail format only on EMAIL rows (same check as the company e-mail: Angular's e-mail validator). */
function contactValueValidator(group: AbstractControl): ValidationErrors | null {
    const { type, value } = (group as ContactGroup).getRawValue();
    if (type !== PersonContactType.EMAIL || !value?.trim()) return null;
    return Validators.email(new FormControl(value.trim()));
}

/**
 * REV-22155 — the person form, for "+ Neue Person" and the profile (pure view).
 *
 * Shows the boxes Personalien, Adresse, Kontaktdaten, Verknüpfte Firmen and Tag, plus the buttons
 * Löschen / Deaktivieren or Aktivieren / Speichern. It loads and saves nothing: the page passes the
 * data in and reacts to `save`, `delete`, `statusChange` and the tag events.
 *
 * Kept in the form itself (display state only): the field values until "Speichern", the open
 * "+ Kontakt hinzufügen" menu, the confirmation dialogs.
 */
@Component({
    selector: 'aumico-person-form',
    // e2e selector contract (e2e/fixtures/persons.ts)
    host: { 'data-testid': 'person-form' },
    templateUrl: './person-form.component.html',
    styleUrls: ['./person-form.component.scss'],
})
export class PersonFormComponent implements OnChanges {
    /** Empty (null) for "+ Neue Person". */
    @Input() person: IPersonForm | null = null;
    @Input() isNew = false;
    @Input() active = true;
    /** Users of the organization (`id` + `name`) for Mandatsleiter / Sachbearbeiter. */
    @Input() users: INameId[] = [];
    /** Companies of the organization for "Verknüpfte Firmen". */
    @Input() companies: { _id: string; name: string }[] = [];
    @Input() tags: ITagDTO[] = [];
    @Input() tagUsage: { id: string; usage: ITagUsageDTO } | null = null;
    /** Passed straight to the tag box so it stops waiting after a failed tag call. */
    @Input() tagError: unknown = null;
    /** While the page saves: "Speichern" is disabled. */
    @Input() saving = false;
    /** With CPMM a changed Sachbearbeiter asks which tasks move along (as on the company profile). */
    @Input() cpmmActive = false;

    @Output() save = new EventEmitter<IPersonForm>();
    @Output() delete = new EventEmitter<void>();
    @Output() statusChange = new EventEmitter<boolean>();
    @Output() tagCreate = new EventEmitter<string>();
    @Output() tagDeleteRequest = new EventEmitter<string>();
    @Output() tagDeleteConfirm = new EventEmitter<string>();
    @Output() tagDeleteCancel = new EventEmitter<void>();
    /** Click on a linked company (not on deleted ones): the page opens its profile. */
    @Output() companyClick = new EventEmitter<string>();

    readonly local = localizationKeys;
    readonly contactType = PersonContactType;

    readonly form = new FormGroup({
        firstName: new FormControl('', { nonNullable: true, validators: [Validators.required, notBlank] }),
        lastName: new FormControl('', { nonNullable: true, validators: [Validators.required, notBlank] }),
        assignee: new FormControl<INameId | null>(null, Validators.required),
        assignedOn: new FormControl<Date | null>(null),
        clerk: new FormControl<INameId | null>(null, Validators.required),
        assignedOnClerk: new FormControl<Date | null>(null),
        street: new FormControl('', { nonNullable: true }),
        additionToAddress: new FormControl('', { nonNullable: true }),
        zip: new FormControl('', { nonNullable: true }),
        city: new FormControl('', { nonNullable: true }),
        contacts: new FormArray<ContactGroup>([]),
    });

    /** Inputs of the "Verknüpfen" row. */
    readonly linkCompany = new FormControl<INameId | null>(null);
    readonly linkRole = new FormControl<INameId | null>(null);

    links: CompanyLink[] = [];
    selectedTagIds: string[] = [];
    roleOptions: INameId[] = [];
    companyOptions: INameId[] = [];
    showDeleteDialog = false;
    showClerkDialog = false;
    readonly tasksUpdateOption = TasksUpdate;
    selectedTaskUpdate = TasksUpdate.UPDATE_TASKS_ASSIGNED_TO_CURRENT;
    private tasksUpdate: TasksUpdate | undefined;
    /** Sachbearbeiter before the open dialog; restored on "Schliessen". */
    private confirmedClerk: INameId | null = null;

    /** "Zuständig seit" as it came in — restored when the same user is chosen again. */
    private originalAssignee: { id: string | null; since: Date | null } = { id: null, since: null };
    private originalClerk: { id: string | null; since: Date | null } = { id: null, since: null };

    constructor(@Inject(I18NEXT_SERVICE) private readonly i18n: ITranslationService) {
        this.roleOptions = ROLE_ORDER.map((role) => ({ id: role, name: this.roleLabel(role) }));
        this.form.controls.assignee.valueChanges.subscribe((user) =>
            this.form.controls.assignedOn.setValue(this.sinceFor(user, this.originalAssignee)),
        );
        this.form.controls.clerk.valueChanges.subscribe((user) => {
            this.form.controls.assignedOnClerk.setValue(this.sinceFor(user, this.originalClerk));
            if (!this.isNew && this.cpmmActive && user && user.id !== this.confirmedClerk?.id) {
                this.selectedTaskUpdate = TasksUpdate.UPDATE_TASKS_ASSIGNED_TO_CURRENT;
                this.showClerkDialog = true;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['companies']) {
            this.companyOptions = this.companies.map((company) => ({ id: company._id, name: company.name }));
        }
        if (changes['person'] || (changes['users'] && this.person)) {
            this.fillForm(this.person);
        }
    }

    get contacts(): FormArray<ContactGroup> {
        return this.form.controls.contacts;
    }

    get canSave(): boolean {
        return this.form.valid && !this.saving;
    }

    /** "Verknüpfen" only with a company and a role, and not for a combination that is already there. */
    get canLink(): boolean {
        const company = this.linkCompany.value;
        const role = this.linkRole.value;
        if (!company || !role) return false;
        return !this.links.some((link) => link.companyId === company.id && link.role === role.id);
    }

    userName(id: string | null): string {
        return this.users.find((user) => user.id === id)?.name ?? '';
    }

    roleLabel(role: PersonCompanyRole): string {
        return this.i18n.t(`aumico_persons_form_role_${role}`);
    }

    addContact(type: PersonContactType): void {
        this.contacts.push(this.contactGroup(type, ''));
    }

    /** The first e-mail and the first phone field always stay; every further contact can be removed. */
    isRemovableContact(index: number): boolean {
        const type = this.contacts.at(index).controls.type.value;
        const firstOfType = this.contacts.controls.findIndex((group) => group.controls.type.value === type);
        return index !== firstOfType;
    }

    removeContact(index: number): void {
        if (this.isRemovableContact(index)) {
            this.contacts.removeAt(index);
        }
    }

    link(): void {
        if (!this.canLink) return;
        const company = this.linkCompany.value as INameId;
        const role = this.linkRole.value as INameId;
        this.links = [
            ...this.links,
            {
                companyId: company.id,
                companyName: company.name,
                role: role.id as PersonCompanyRole,
                companyDeleted: false,
            },
        ];
        // Both dropdowns empty again for the next link.
        this.linkCompany.setValue(null);
        this.linkRole.setValue(null);
    }

    unlink(index: number): void {
        this.links = this.links.filter((_, i) => i !== index);
    }

    onTagSelectionChange(ids: string[]): void {
        this.selectedTagIds = ids;
    }

    onSave(): void {
        if (!this.canSave) return;
        const value = this.form.getRawValue();
        this.save.emit({
            firstName: value.firstName.trim(),
            lastName: value.lastName.trim(),
            assignee: value.assignee?.id ?? null,
            assignedOn: value.assignedOn,
            clerk: value.clerk?.id ?? null,
            assignedOnClerk: value.assignedOnClerk,
            street: value.street,
            additionToAddress: value.additionToAddress,
            zip: value.zip,
            city: value.city,
            contacts: value.contacts
                .map((contact) => ({ type: contact.type, value: contact.value.trim() }))
                .filter((contact) => contact.value !== ''),
            companies: this.links,
            tags: this.selectedTagIds,
            tasksUpdate: this.tasksUpdate,
        });
    }

    confirmClerkChange(): void {
        this.tasksUpdate = this.selectedTaskUpdate;
        this.confirmedClerk = this.form.controls.clerk.value;
        this.showClerkDialog = false;
    }

    cancelClerkChange(): void {
        this.form.controls.clerk.setValue(this.confirmedClerk, { emitEvent: false });
        this.form.controls.assignedOnClerk.setValue(this.sinceFor(this.confirmedClerk, this.originalClerk));
        this.showClerkDialog = false;
    }

    confirmDelete(confirmed: boolean): void {
        this.showDeleteDialog = false;
        if (confirmed) this.delete.emit();
    }

    activate(): void {
        this.statusChange.emit(true);
    }

    private fillForm(person: IPersonForm | null): void {
        const assignee = this.toUser(person?.assignee);
        const clerk = this.toUser(person?.clerk);
        this.originalAssignee = { id: assignee?.id ?? null, since: person?.assignedOn ?? null };
        this.originalClerk = { id: clerk?.id ?? null, since: person?.assignedOnClerk ?? null };
        this.confirmedClerk = clerk;
        this.tasksUpdate = undefined;

        this.form.patchValue(
            {
                firstName: person?.firstName ?? '',
                lastName: person?.lastName ?? '',
                assignee,
                assignedOn: person?.assignedOn ?? null,
                clerk,
                assignedOnClerk: person?.assignedOnClerk ?? null,
                street: person?.street ?? '',
                additionToAddress: person?.additionToAddress ?? '',
                zip: person?.zip ?? '',
                city: person?.city ?? '',
            },
            { emitEvent: false },
        );

        // At the start one e-mail and one phone field (prototype), plus every further stored contact.
        this.contacts.clear();
        const stored = person?.contacts ?? [];
        for (const type of [PersonContactType.EMAIL, PersonContactType.PHONE]) {
            if (!stored.some((contact) => contact.type === type)) {
                this.contacts.push(this.contactGroup(type, ''));
            }
        }
        for (const contact of stored) {
            this.contacts.push(this.contactGroup(contact.type, contact.value));
        }
        this.sortContacts();

        this.links = [...(person?.companies ?? [])];
        this.selectedTagIds = [...(person?.tags ?? [])];
    }

    /** E-mails first, then phones — each type stays together like in the prototype. */
    private sortContacts(): void {
        const groups = this.contacts.controls.slice();
        groups.sort((a, b) => (a.controls.type.value === b.controls.type.value ? 0 : a.controls.type.value === PersonContactType.EMAIL ? -1 : 1));
        this.contacts.clear();
        groups.forEach((group) => this.contacts.push(group));
    }

    private contactGroup(type: PersonContactType, value: string): ContactGroup {
        return new FormGroup(
            {
                type: new FormControl(type, { nonNullable: true }),
                value: new FormControl(value, { nonNullable: true }),
            },
            { validators: contactValueValidator },
        );
    }

    private toUser(id: string | null | undefined): INameId | null {
        if (!id) return null;
        return this.users.find((user) => user.id === id) ?? { id, name: '' };
    }

    /** Same user as stored → keep the stored date; another user → today; none → no date. */
    private sinceFor(user: INameId | null, original: { id: string | null; since: Date | null }): Date | null {
        if (!user) return null;
        return user.id === original.id ? original.since : new Date();
    }
}

function notBlank(control: AbstractControl): ValidationErrors | null {
    return typeof control.value === 'string' && control.value.trim() === '' && control.value !== ''
        ? { blank: true }
        : null;
}
